import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { financeReportService } from '../../services/financeReport.service';
import { BarChart3 } from 'lucide-react';

export const FinanceSummaryTab: React.FC = () => {
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [queryDates, setQueryDates] = useState({ startDate, endDate });

  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['finance-summary', queryDates.startDate, queryDates.endDate],
    queryFn: () => financeReportService.getSummary(queryDates.startDate, queryDates.endDate),
  });

  const handleGenerate = () => {
    setQueryDates({ startDate, endDate });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <Input
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <div className="flex items-end">
          <Button onClick={handleGenerate} className="w-full">
            <BarChart3 size={16} className="mr-2" />
            Generate Summary
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-8 text-gray-500">Loading financial summary...</div>
      )}

      {error && (
        <div className="text-center py-8 text-error">Failed to load financial summary</div>
      )}

      {summary && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Invoiced</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(summary.totalInvoiced)}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.invoiceCount} invoices</p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Paid</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(summary.totalPaid)}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.paymentCount} payments</p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Outstanding</p>
              <p className="text-2xl font-bold text-yellow-700">{formatCurrency(summary.totalOutstanding)}</p>
              <p className="text-xs text-gray-500 mt-1">Unpaid balance</p>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Allowances</p>
              <p className="text-2xl font-bold text-purple-700">{formatCurrency(summary.totalAllowances)}</p>
              <p className="text-xs text-gray-500 mt-1">Driver expenses</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-primary-green to-green-600 p-6 rounded-lg text-white">
            <p className="text-sm opacity-90 mb-2">Net Profit</p>
            <p className="text-4xl font-bold">{formatCurrency(summary.netProfit)}</p>
            <p className="text-sm opacity-75 mt-2">
              Total Paid - Total Allowances
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
            <div>
              <p className="text-sm text-gray-600">Collection Rate</p>
              <p className="text-xl font-semibold">
                {summary.totalInvoiced > 0
                  ? ((summary.totalPaid / summary.totalInvoiced) * 100).toFixed(1)
                  : 0}%
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Profit Margin</p>
              <p className="text-xl font-semibold">
                {summary.totalPaid > 0
                  ? ((summary.netProfit / summary.totalPaid) * 100).toFixed(1)
                  : 0}%
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg Invoice Value</p>
              <p className="text-xl font-semibold">
                {summary.invoiceCount > 0
                  ? formatCurrency(summary.totalInvoiced / summary.invoiceCount)
                  : formatCurrency(0)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
